import React, { createContext, useContext, useMemo, useState } from 'react';

import { getIsLaunchableCrm, getServiceIdFromIntegration } from '@aline/common';
import type { TooltipProps } from '@mui/material';
import { Link, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import clsx from 'clsx';
import { CircleAlert, CircleX, ExternalLink } from 'lucide-react';
import { AlineButton } from 'src/components/generic/AlineButton';
import { AlineDialog } from 'src/components/generic/AlineDialog';
import { AlineDialogButtons } from 'src/components/generic/AlineDialogButtons';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { CRMConnectedIcon } from 'src/components/icons/CRMConnectedIcon';
import { Checklist } from 'src/components/send-for-signature/components/Checklist';
import type { ChecklistItem } from 'src/components/send-for-signature/components/Checklist';
import { DialogHeader } from 'src/components/send-for-signature/components/DialogHeader';
import { docsRoute } from 'src/config';
import { Apollo } from 'src/gql/generated';
import { INTEGRATION_DISPLAY_NAME } from 'src/helpers/integration-display-name';
import type {
  CrmConnectRequest,
  CrmWithConnectRequirements,
  IntegrationRecordMap,
} from 'src/hooks';
import {
  getHasCrmConnectRequirements,
  getIntegrationHelpNote,
  getIntegrationVaultGate,
  getIsApiDeckConnectionCallable,
  useAlineCrmSession,
  useIntegrationReadiness,
} from 'src/hooks';
import { CrmIntegrationIcon } from 'src/images/integrations/CrmIntegrationIcon';
import { makeStyles } from 'tss-react/mui';

type LaunchIntegrationSeverity = 'error' | 'warning';

/** What the connect dialog says about one CRM: its setup guide and what connecting requires. */
interface CrmAdminCopy {
  readonly guideUrl: string;
  readonly requirements: readonly ChecklistItem[];
}

interface CrmConnectContent {
  /** Null for a CRM without a setup guide of its own. */
  readonly guideUrl: string | null;
  readonly icon: React.ReactNode;
  readonly name: string;
  readonly requirements: readonly ChecklistItem[];
}

/** Keyed on `CrmWithConnectRequirements` so a CRM cannot pause on the dialog without its copy. */
const CRM_CONNECT_CONTENT: Record<CrmWithConnectRequirements, CrmAdminCopy> = {
  [Apollo.Integration.SalesForce]: {
    guideUrl:
      'https://docs.aline.co/en/articles/15800062-aline-for-salesforce-setting-up-the-managed-package',
    requirements: [
      {
        description:
          'Connecting has to be done from a System Administrator account, or a profile that can assign permission sets, edit page layouts, and use Lightning App Builder. Have those credentials ready.',
        title: 'Sign in as a System Administrator',
      },
      {
        description:
          'Version 0.6.0 or later. Your Salesforce admin installs it from the production or sandbox link in the setup guide.',
        title: 'Install the latest Aline package',
      },
    ],
  },
};

/**
 * The setup guide for a CRM. A launchable CRM without a guide of its own (HubSpot) has none; any
 * other CRM falls back to the general docs.
 */
export function getCrmSetupGuideUrl(integration: Apollo.Integration): string | null {
  if (getHasCrmConnectRequirements(integration)) return CRM_CONNECT_CONTENT[integration].guideUrl;
  return getIsLaunchableCrm(integration) ? null : docsRoute;
}

/**
 * Copy, icon, and guide for the connect dialog. The last CRM is held after the request clears so
 * the dialog animates out still showing it instead of blanking first. Only what the CRM itself
 * needs is listed: the Aline-admin gate sits on the control that opens the dialog. Add a CRM by
 * extending `CRM_CONNECT_CONTENT`.
 */
export function useCrmConnectContent(integration: Apollo.Integration | null): CrmConnectContent {
  const [heldIntegration, setHeldIntegration] = useState(integration);
  if (integration && integration !== heldIntegration) setHeldIntegration(integration);
  if (!heldIntegration) return { guideUrl: null, icon: null, name: '', requirements: [] };

  return {
    guideUrl: getCrmSetupGuideUrl(heldIntegration),
    icon: <CrmIntegrationIcon integration={heldIntegration} />,
    name: INTEGRATION_DISPLAY_NAME[heldIntegration],
    requirements: getHasCrmConnectRequirements(heldIntegration)
      ? CRM_CONNECT_CONTENT[heldIntegration].requirements
      : [],
  };
}

/** What a control inside a `CrmVaultAction` is handed: whether it is gated, and what opening does. */
export interface CrmVaultControlState {
  readonly disabled: boolean;
  readonly disabledReason: string | null;
  readonly hint: string | null;
  readonly muted: boolean;
  readonly open: () => void;
}

const CrmVaultContext = createContext<CrmVaultControlState | null>(null);

/**
 * Everything a control that opens the org's CRM Vault needs around it: the admin-only gate and
 * the first-connect requirements dialog. Render the control itself through
 * `CrmVaultAction.Control`, which may sit anywhere below, including inside a menu or an alert
 * that unmounts when it closes; the dialog stays mounted here.
 */
export function CrmVaultAction({
  children,
  integration,
}: {
  readonly children: React.ReactNode;
  readonly integration: Apollo.Integration;
}): JSX.Element {
  const { canConnectCrm, crmConnectRequest, openVault } = useAlineCrmSession();
  const { ready } = useIntegrationReadiness(integration);
  const state = useMemo<CrmVaultControlState>(() => {
    const gate = getIntegrationVaultGate({ canConnectCrm, ready });
    return {
      disabled: gate.blocked,
      disabledReason: gate.reason,
      hint: gate.reason ?? getIntegrationHelpNote({ integration }),
      muted: gate.muted,
      open: () => {
        if (!gate.blocked) void openVault(getServiceIdFromIntegration(integration));
      },
    };
  }, [canConnectCrm, integration, openVault, ready]);

  return (
    <CrmVaultContext.Provider value={state}>
      {children}
      <CrmConnectRequirementsHost request={crmConnectRequest} />
    </CrmVaultContext.Provider>
  );
}

/**
 * The gated control: a tooltip around a span with the gate's reason or, outside production, the
 * development-app note, since a disabled control fires no pointer events of its own. A control that shows the gate itself, such as a card
 * marked `aria-disabled`, turns the tooltip off.
 */
function CrmVaultControl({
  children,
  placement = 'top',
  tooltip = true,
}: {
  readonly children: (state: CrmVaultControlState) => React.ReactNode;
  readonly placement?: TooltipProps['placement'];
  readonly tooltip?: boolean;
}): JSX.Element {
  const state = useContext(CrmVaultContext);
  if (!state) throw new Error('CrmVaultAction.Control must render inside a CrmVaultAction.');
  const control = children(state);
  if (!tooltip) return <>{control}</>;

  return (
    <AlineTooltip arrow placement={placement} title={state.hint ?? ''}>
      <span>{control}</span>
    </AlineTooltip>
  );
}

CrmVaultAction.Control = CrmVaultControl;

const useStyles = makeStyles()(theme => ({
  badge: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    borderRadius: '50%',
    display: 'inline-flex',
    height: 16,
    justifyContent: 'center',
    width: 16,
  },
  checkMark: {
    bottom: -4,
    height: 16,
    position: 'absolute',
    right: -4,
    width: 16,
  },
  connectionRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.75),
    justifyContent: 'space-between',
    minHeight: 48,
  },
  errorLabel: {
    color: theme.palette.error.main,
  },
  icon: {
    display: 'block',
    height: 22,
    objectFit: 'contain',
    width: 22,
  },
  iconTile: {
    alignItems: 'center',
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: theme.spacing(1),
    boxSizing: 'border-box',
    display: 'flex',
    flexShrink: 0,
    height: 40,
    justifyContent: 'center',
    position: 'relative',
    width: 40,
  },
  loadingButton: {
    borderRadius: 16,
  },
  requirementsGuideLink: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    display: 'inline-flex',
    fontSize: 14,
    fontWeight: 600,
    gap: theme.spacing(0.75),
  },
  root: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  successLabel: {
    color: theme.palette.success.main,
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    minWidth: 0,
  },
  warningLabel: {
    color: theme.palette.warning.dark,
  },
}));

/**
 * The checklist a first-time CRM connect shows before the Vault opens. Icon, copy, and guide
 * come from the CRM the pending request is for, so a new CRM needs no change here.
 */
export function CrmConnectRequirementsHost({
  request,
}: {
  request: CrmConnectRequest | null;
}): JSX.Element {
  const { classes } = useStyles();
  const { guideUrl, icon, name, requirements } = useCrmConnectContent(request?.integration ?? null);
  const onCancel = () => request?.onCancel();

  return (
    <AlineDialog
      fullWidth
      maxWidth="sm"
      open={Boolean(request)}
      title={
        <DialogHeader
          icon={icon}
          subtitle={`Make sure these are done before you sign in to ${name}.`}
          title={`Before you connect ${name}`}
        />
      }
      onClose={onCancel}
    >
      <Stack sx={{ gap: 1.5 }}>
        <Checklist numbered items={requirements} />
        {guideUrl !== null && (
          <Link
            className={classes.requirementsGuideLink}
            href={guideUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            <ExternalLink size={14} />
            {`Open the ${name} setup guide`}
          </Link>
        )}
      </Stack>
      <AlineDialogButtons
        cancelHandler={onCancel}
        submitHandler={() => void request?.onConfirm()}
        submitText="Continue"
      />
    </AlineDialog>
  );
}

function getLaunchIntegrationStatus({
  isConnectionCallable,
  isRecordLinked,
}: {
  isConnectionCallable: boolean;
  isRecordLinked: boolean;
}): { label: string; severity: LaunchIntegrationSeverity | null } {
  if (!isConnectionCallable) return { label: 'Disconnected', severity: 'error' };
  if (!isRecordLinked) return { label: 'Connected - Unlinked', severity: 'warning' };
  return { label: 'Connected', severity: null };
}

export function getLaunchIntegrationIssues({
  connections,
  integrationRecordMap,
}: {
  connections: Apollo.ApiDeckConnectionSimpleFragment[];
  integrationRecordMap: IntegrationRecordMap;
}) {
  return connections.flatMap(connection => {
    const { severity } = getLaunchIntegrationStatus({
      isConnectionCallable: getIsApiDeckConnectionCallable(connection),
      isRecordLinked: Boolean(integrationRecordMap[connection.integration]),
    });
    return severity ? [severity] : [];
  });
}

export function ReconnectIntegrationButton({
  buttonClassName,
  integration,
  size,
}: {
  integration: Apollo.Integration;
  buttonClassName?: string;
  size?: 'small' | 'medium' | 'large';
}) {
  return (
    <CrmVaultAction integration={integration}>
      <CrmVaultAction.Control>
        {({ disabled, open }) => (
          <AlineButton
            buttonClassName={buttonClassName}
            color="primary"
            disabled={disabled}
            size={size}
            variant="outlined"
            onClick={open}
          >
            Connect
          </AlineButton>
        )}
      </CrmVaultAction.Control>
    </CrmVaultAction>
  );
}

export function UnifiedConnection({
  className,
  connection,
  isConnectionCallable,
  isRecordLinked,
}: {
  connection: Apollo.ApiDeckConnection;
  isConnectionCallable: boolean;
  isRecordLinked: boolean;
  className?: string;
}) {
  const { classes } = useStyles();
  const status = getLaunchIntegrationStatus({
    isConnectionCallable,
    isRecordLinked,
  });

  return (
    <div className={clsx(className, classes.root)}>
      <div className={classes.iconTile}>
        <ConnectionGlyph connection={connection} />
        <ConnectionStatusMark severity={status.severity} />
      </div>
      <div className={classes.text}>
        <Typography noWrap variant="subtitle2">
          {INTEGRATION_DISPLAY_NAME[connection.integration]}
        </Typography>
        <Typography
          className={
            {
              error: classes.errorLabel,
              success: classes.successLabel,
              warning: classes.warningLabel,
            }[status.severity ?? 'success']
          }
          color="inherit"
          variant="caption"
        >
          {status.label}
        </Typography>
      </div>
    </div>
  );
}

function ConnectionGlyph({ connection }: { connection: Apollo.ApiDeckConnection }) {
  const { classes } = useStyles();

  if (connection.icon) return <img alt="" className={classes.icon} src={connection.icon} />;
  return <CrmIntegrationIcon className={classes.icon} integration={connection.integration} />;
}

function ConnectionStatusMark({ severity }: { severity: LaunchIntegrationSeverity | null }) {
  const { classes, cx } = useStyles();
  const theme = useTheme();

  if (!severity) return <CRMConnectedIcon className={classes.checkMark} />;

  const BadgeIcon = severity === 'error' ? CircleX : CircleAlert;

  return (
    <span className={cx(classes.badge, classes.checkMark)}>
      <BadgeIcon
        aria-hidden
        color={theme.palette.common.white}
        fill={severity === 'error' ? theme.palette.error.main : theme.palette.warning.dark}
        size={19.2}
        strokeWidth={2.25}
      />
    </span>
  );
}

export function WorkflowIntegrationsLoading() {
  const { classes } = useStyles();
  return (
    <>
      {Array.from({ length: 2 }).map((_num, index) => (
        <div key={index} className={classes.connectionRow}>
          <div className={classes.root}>
            <Skeleton height={40} variant="rounded" width={40} />
            <div className={classes.text}>
              <Skeleton height={20} variant="text" width={72} />
              <Skeleton height={16} variant="text" width={96} />
            </div>
          </div>
          <Skeleton
            className={classes.loadingButton}
            height={32}
            variant="rectangular"
            width={120}
          />
        </div>
      ))}
    </>
  );
}
